import { API_BASE } from './api-base';
import { apiFetch } from './api-client';

// Read the meta-site's Site Properties language via /api/site-language — the single source of
// truth `pages/_app.tsx` resolves the active UI locale from on mount. Best-effort: any failure
// (network, unauthenticated, no tenant/metasite yet, property unset) resolves to `null`, which the
// caller treats as "use the default" (`resolveLocale(null)` → `en`).
export async function fetchSiteLanguage(): Promise<string | null> {
  return (await fetchSiteLocale()).languageCode;
}

// The account's REGION (ISO 3166-1 alpha-2), from the same Site Properties read. Numeric date order
// follows it rather than the browser's locale — see `lib/date-locale.ts`.
export async function fetchSiteCountry(): Promise<string | null> {
  return (await fetchSiteLocale()).country;
}

// One read behind both accessors; same best-effort contract — any failure resolves to nulls, which
// every caller treats as "no account signal, use the browser's".
async function fetchSiteLocale(): Promise<{ languageCode: string | null; country: string | null }> {
  const none = { languageCode: null, country: null };
  try {
    const res = await apiFetch(`${API_BASE}/api/site-language`, { credentials: 'include' });
    if (!res.ok) return none;
    const data = (await res.json()) as { languageCode?: unknown; country?: unknown } | null;
    return {
      languageCode: typeof data?.languageCode === 'string' ? data.languageCode : null,
      country: typeof data?.country === 'string' ? data.country : null,
    };
  } catch {
    return none;
  }
}

// Persist the user's language pick to their meta-site Site Properties via /api/site-language
// (SitePropertiesService, `language` + `locale.languageCode`). Site Properties is the single
// source of truth for the active UI locale (see `fetchSiteLanguage`/`pages/_app.tsx`); this is
// how a Settings pick gets there.
//
// Best-effort and non-blocking: the UI has already switched locale locally, so a failed backend
// write must never block or revert that. We log and swallow.
export async function persistSiteLanguage(languageCode: string): Promise<void> {
  try {
    const res = await apiFetch(`${API_BASE}/api/site-language`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ languageCode }),
    });
    if (!res.ok) {
      // In `next dev` the /api/* calls proxy to the DEPLOYED prod handler, so a brand-new route 404s
      // until it ships; a deployed run would show the backend's actual error here instead.
      const detail = await res.text().catch(() => '');
      console.error(`[persistSiteLanguage] failed: HTTP ${res.status}`, detail.slice(0, 500));
    }
  } catch (err) {
    console.error('[persistSiteLanguage] failed', err);
  }
}
